import styled from '@emotion/styled';
import React, { useCallback, useEffect, useState, useRef } from 'react';
import {
  Image,
  Pressable,
  ImageStyle,
  StyleProp,
  ViewStyle,
} from 'react-native';
import { useTwilio } from '@/services/twilio/TwilioProvider';
import useStore from '@/stores/useStore';
import { useShallow } from 'zustand/react/shallow';
import {
  black,
  green,
  lightGreen,
  red,
  sand,
  videoCellBackground,
  white,
  accent,
} from '@/shared/styles/colors';
import SentryService from '@/services/sentry/Sentry.service';
import Button from '@/shared/components/Button';
import { FONT_STYLES, ZIndex } from '@/shared/styles/constants';
import { useSessionTimer } from '@/shared/hooks/useSessionTimer';
import { useVideoHeartbeat } from '@/shared/hooks/useVideoHeartbeat';
import { router } from 'expo-router';
import { ROUTES } from '@/shared/constants/routes';
import EndCallModal from '@/shared/components/EndCallModal';
import { useQuery } from '@apollo/client';
import { GET_CONSULTATIONS } from '@/shared/queries';
import { QueryResult } from '@/shared/types/generated';
import TwoMinuteWarningModal from '@/shared/components/video/TwoMinuteWarningModal';
import {
  RemoteParticipant,
  RemoteVideoTrack,
  RemoteAudioTrack,
  RemoteTrack,
  LocalVideoTrack,
  LocalAudioTrack,
  Room,
} from 'twilio-video';
import { extractDisplayNameFromIdentity } from '@/shared/utils';
import { cleanupVideoSessionState } from '@/shared/utils/videoUtils';
import { VIDEO_CHAT_WEB_PANEL_WIDTH } from '@/shared/constants/videoSession';
import VideoChat from '@/shared/components/video/VideoChat';

const TwilioVideoFeed = () => {
  const { endSession, sessionStartTime, room: roomFromContext } = useTwilio();
  const room = roomFromContext as Room | null;
  const { formattedTime } = useSessionTimer(sessionStartTime);

  const {
    setIsInSession,
    setActiveSessionName,
    activeSessionName,
    otherUserDisconnected,
    setOtherUserDisconnected,
    isStylist,
    sessionChannelUrl,
  } = useStore(
    useShallow((state) => ({
      setIsInSession: state.setIsInSession,
      setActiveSessionName: state.setActiveSessionName,
      activeSessionName: state.activeSessionName,
      otherUserDisconnected: state.otherUserDisconnected,
      setOtherUserDisconnected: state.setOtherUserDisconnected,
      isStylist: state.isStylist,
      sessionChannelUrl: state.sessionChannelUrl,
    })),
  );

  const localVideoRef = useRef<HTMLDivElement>(null);
  const remoteVideoRef = useRef<HTMLDivElement>(null);
  const isIntentionalDisconnectRef = useRef(false);

  const [remoteParticipant, setRemoteParticipant] =
    useState<RemoteParticipant | null>(null);
  const [isAudioMuted, setIsAudioMuted] = useState(false);
  const [isVideoMuted, setIsVideoMuted] = useState(false);
  const [isRemoteAudioMuted, setIsRemoteAudioMuted] = useState(true);
  const [isRemoteVideoMuted, setIsRemoteVideoMuted] = useState(false);
  const [isEndCallModalVisible, setIsEndCallModalVisible] = useState(false);
  const [isTwoMinuteWarningModalVisible, setIsTwoMinuteWarningModalVisible] =
    useState(false);
  const [hasShownTwoMinuteWarning, setHasShownTwoMinuteWarning] =
    useState(false);
  const [isChatPanelOpen, setIsChatPanelOpen] = useState(false);
  const autoEndTimeoutRef = React.useRef<ReturnType<typeof setTimeout>>();

  // Fetch consultations to get endTime for active session
  const skipQuery = !activeSessionName || Boolean(isStylist);
  const { data: consultationsData, loading: consultationsLoading } = useQuery<
    QueryResult<'consultations'>
  >(GET_CONSULTATIONS, {
    fetchPolicy: 'cache-first',
    skip: skipQuery,
  });

  // Find consultation matching active session
  const activeConsultation = consultationsData?.consultations?.find(
    (consultation) =>
      consultation.videoSession?.sessionName === activeSessionName,
  );

  // Start video session heartbeat
  useVideoHeartbeat({
    sessionName: activeSessionName,
    isActive: true,
  });

  // Attach local video track
  useEffect(() => {
    if (!room || !localVideoRef.current) {
      return;
    }

    const localParticipant = room.localParticipant;
    const videoPublication = Array.from(
      localParticipant.videoTracks.values(),
    )[0] as { track: LocalVideoTrack } | undefined;
    const videoTrack = videoPublication?.track || null;

    if (videoTrack && localVideoRef.current) {
      const videoElement = videoTrack.attach();
      localVideoRef.current.innerHTML = '';
      localVideoRef.current.appendChild(videoElement);
    }

    return () => {
      if (videoTrack) {
        videoTrack.detach().forEach((element) => element.remove());
      }
    };
  }, [room]);

  // Handle remote participants
  useEffect(() => {
    if (!room) {
      return;
    }

    // Store track listeners for cleanup
    const videoTrackListeners = new Map<
      RemoteVideoTrack,
      {
        onDisabled: () => void;
        onEnabled: () => void;
      }
    >();

    const audioTrackListeners = new Map<
      RemoteAudioTrack,
      {
        onDisabled: () => void;
        onEnabled: () => void;
      }
    >();

    // Store audio elements for cleanup
    const audioElements = new Map<RemoteAudioTrack, HTMLAudioElement>();

    const participantConnected = (participant: RemoteParticipant) => {
      try {
        SentryService.captureInfo('Twilio: Participant connected', {
          extra: {
            identity: participant.identity,
            sid: participant.sid,
          },
        });
        setRemoteParticipant(participant);

        // Helper to attach video track enable/disable listeners
        const attachVideoTrackListeners = (videoTrack: RemoteVideoTrack) => {
          const onDisabled = () => {
            SentryService.captureInfo('Twilio: Remote video track disabled', {
              extra: { trackSid: videoTrack.sid },
            });
            setIsRemoteVideoMuted(true);
          };

          const onEnabled = () => {
            SentryService.captureInfo('Twilio: Remote video track enabled', {
              extra: { trackSid: videoTrack.sid },
            });
            setIsRemoteVideoMuted(false);
          };

          videoTrack.on('disabled', onDisabled);
          videoTrack.on('enabled', onEnabled);

          // Store listeners for cleanup
          videoTrackListeners.set(videoTrack, { onDisabled, onEnabled });
        };

        // Helper to remove video track listeners
        const removeVideoTrackListeners = (videoTrack: RemoteVideoTrack) => {
          const listeners = videoTrackListeners.get(videoTrack);
          if (listeners) {
            videoTrack.off('disabled', listeners.onDisabled);
            videoTrack.off('enabled', listeners.onEnabled);
            videoTrackListeners.delete(videoTrack);
          }
        };

        // Helper to attach audio track enable/disable listeners
        const attachAudioTrackListeners = (audioTrack: RemoteAudioTrack) => {
          const onDisabled = () => {
            SentryService.captureInfo('Twilio: Remote audio track disabled', {
              extra: { trackSid: audioTrack.sid },
            });
            setIsRemoteAudioMuted(true);
          };

          const onEnabled = () => {
            SentryService.captureInfo('Twilio: Remote audio track enabled', {
              extra: { trackSid: audioTrack.sid },
            });
            setIsRemoteAudioMuted(false);
          };

          audioTrack.on('disabled', onDisabled);
          audioTrack.on('enabled', onEnabled);

          // Store listeners for cleanup
          audioTrackListeners.set(audioTrack, { onDisabled, onEnabled });
        };

        // Helper to remove audio track listeners
        const removeAudioTrackListeners = (audioTrack: RemoteAudioTrack) => {
          const listeners = audioTrackListeners.get(audioTrack);
          if (listeners) {
            audioTrack.off('disabled', listeners.onDisabled);
            audioTrack.off('enabled', listeners.onEnabled);
            audioTrackListeners.delete(audioTrack);
          }
        };

        // Subscribe to existing tracks
        participant.tracks.forEach((publication) => {
          if (publication.track) {
            if (publication.track.kind === 'video') {
              const videoTrack = publication.track as RemoteVideoTrack;
              if (remoteVideoRef.current) {
                try {
                  const videoElement = videoTrack.attach();
                  remoteVideoRef.current.innerHTML = '';
                  remoteVideoRef.current.appendChild(videoElement);

                  // Set initial muted state based on track's enabled state
                  setIsRemoteVideoMuted(!videoTrack.isEnabled);

                  // Attach track listeners
                  attachVideoTrackListeners(videoTrack);
                } catch (error) {
                  SentryService.captureError(
                    'Error attaching existing video track',
                    error,
                  );
                }
              }
            } else if (publication.track.kind === 'audio') {
              const audioTrack = publication.track as RemoteAudioTrack;
              try {
                // Attach audio track and append to DOM for playback
                const audioElement = audioTrack.attach();
                document.body.appendChild(audioElement);
                audioElements.set(audioTrack, audioElement);

                // Set initial muted state based on track's enabled state
                setIsRemoteAudioMuted(!audioTrack.isEnabled);

                // Attach audio track listeners
                attachAudioTrackListeners(audioTrack);
              } catch (error) {
                SentryService.captureError(
                  'Error attaching existing audio track',
                  error,
                );
              }
            }
          }
        });

        // Subscribe to future tracks
        const onTrackSubscribed = (track: RemoteTrack) => {
          try {
            if (track.kind === 'video' && remoteVideoRef.current) {
              const videoTrack = track as RemoteVideoTrack;
              const videoElement = videoTrack.attach();
              remoteVideoRef.current.innerHTML = '';
              remoteVideoRef.current.appendChild(videoElement);

              // Set initial muted state based on track's enabled state
              setIsRemoteVideoMuted(!videoTrack.isEnabled);

              // Attach track listeners
              attachVideoTrackListeners(videoTrack);
            } else if (track.kind === 'audio') {
              const audioTrack = track as RemoteAudioTrack;

              // Attach audio track and append to DOM for playback
              const audioElement = audioTrack.attach();
              document.body.appendChild(audioElement);
              audioElements.set(audioTrack, audioElement);

              // Set initial muted state based on track's enabled state
              setIsRemoteAudioMuted(!audioTrack.isEnabled);

              // Attach audio track listeners
              attachAudioTrackListeners(audioTrack);
            }
          } catch (error) {
            SentryService.captureError(
              `Error attaching ${track.kind} track`,
              error,
            );
          }
        };

        const onTrackUnsubscribed = (track: RemoteTrack) => {
          try {
            if (track.kind === 'video') {
              const videoTrack = track as RemoteVideoTrack;

              // Remove track listeners before detaching
              removeVideoTrackListeners(videoTrack);

              track
                .detach()
                .forEach((element: HTMLMediaElement) => element.remove());
              setIsRemoteVideoMuted(true);
            } else if (track.kind === 'audio') {
              const audioTrack = track as RemoteAudioTrack;

              // Remove track listeners before detaching
              removeAudioTrackListeners(audioTrack);

              // Remove audio element from our map
              audioElements.delete(audioTrack);

              track
                .detach()
                .forEach((element: HTMLMediaElement) => element.remove());
              setIsRemoteAudioMuted(true);
            }
          } catch (error) {
            SentryService.captureError(
              `Error detaching ${track.kind} track`,
              error,
            );
          }
        };

        participant.on('trackSubscribed', onTrackSubscribed);
        participant.on('trackUnsubscribed', onTrackUnsubscribed);
      } catch (error) {
        SentryService.captureError(
          'Error in participantConnected handler',
          error,
        );
      }
    };

    const participantDisconnected = (participant: RemoteParticipant) => {
      SentryService.captureInfo('Twilio: Participant disconnected', {
        extra: {
          identity: participant.identity,
          sid: participant.sid,
        },
      });

      // Clean up all video track listeners
      videoTrackListeners.forEach((listeners, track) => {
        track.off('disabled', listeners.onDisabled);
        track.off('enabled', listeners.onEnabled);
      });
      videoTrackListeners.clear();

      // Clean up all audio track listeners
      audioTrackListeners.forEach((listeners, track) => {
        track.off('disabled', listeners.onDisabled);
        track.off('enabled', listeners.onEnabled);
      });
      audioTrackListeners.clear();

      // Clean up audio elements
      audioElements.clear();

      setRemoteParticipant(null);
    };

    room.on('participantConnected', participantConnected);
    room.on('participantDisconnected', participantDisconnected);

    // Handle existing participants
    room.participants.forEach(participantConnected);

    return () => {
      room.off('participantConnected', participantConnected);
      room.off('participantDisconnected', participantDisconnected);

      // Clean up all video track listeners on unmount
      videoTrackListeners.forEach((listeners, track) => {
        track.off('disabled', listeners.onDisabled);
        track.off('enabled', listeners.onEnabled);
      });
      videoTrackListeners.clear();

      // Clean up all audio track listeners on unmount
      audioTrackListeners.forEach((listeners, track) => {
        track.off('disabled', listeners.onDisabled);
        track.off('enabled', listeners.onEnabled);
      });
      audioTrackListeners.clear();

      // Clean up audio elements on unmount
      audioElements.clear();

      // Clean up participant listeners
      room.participants.forEach((participant) => {
        participant.removeAllListeners('trackSubscribed');
        participant.removeAllListeners('trackUnsubscribed');
      });
    };
  }, [room]);

  // Handle room disconnection
  useEffect(() => {
    if (!room) {
      return;
    }

    const handleDisconnected = (
      disconnectedRoom: Room,
      error?: Error | null,
    ) => {
      const isIntentional = isIntentionalDisconnectRef.current;
      const twilioError = error as { code?: number; message?: string } | null;

      SentryService.captureInfo('Twilio: Room disconnected', {
        extra: {
          roomName: disconnectedRoom.name,
          roomSid: disconnectedRoom.sid,
          error: twilioError?.message,
          errorCode: twilioError?.code,
          isIntentional,
        },
      });

      // If this is an intentional disconnect, skip state cleanup
      // The calling code (leaveSession/exitCallOnly) will handle navigation
      if (isIntentional) {
        isIntentionalDisconnectRef.current = false;
        return;
      }

      cleanupVideoSessionState();
      setRemoteParticipant(null);
    };

    room.on('disconnected', handleDisconnected);

    return () => {
      room.off('disconnected', handleDisconnected);
    };
  }, [room, setIsInSession, setActiveSessionName]);

  const checkTimeOutRef = () => {
    if (autoEndTimeoutRef.current) {
      clearTimeout(autoEndTimeoutRef.current);
      autoEndTimeoutRef.current = undefined;
    }
  };

  const leaveSession = useCallback(async () => {
    checkTimeOutRef();

    // Mark as intentional disconnect so handleDisconnected doesn't interfere
    isIntentionalDisconnectRef.current = true;

    try {
      await endSession();
      cleanupVideoSessionState();

      // @ts-expect-error - expo-router query parameter typing
      router.replace(`${ROUTES.SESSIONS.path}?showPostCall=true`);
    } catch (error) {
      SentryService.captureError('Error leaving video session', error);

      // Stop all local tracks before disconnecting
      if (room) {
        room.localParticipant.tracks.forEach((publication) => {
          const track = publication.track;
          if (track && track.kind !== 'data') {
            track.stop();
          }
        });
        room.disconnect();
      }

      cleanupVideoSessionState();

      router.replace(ROUTES.SESSIONS.path);
    }
  }, [endSession, room]);

  const exitCallOnly = useCallback(async () => {
    checkTimeOutRef();

    // Mark as intentional disconnect so handleDisconnected doesn't interfere
    isIntentionalDisconnectRef.current = true;

    try {
      // Stop all local tracks before disconnecting
      if (room) {
        room.localParticipant.tracks.forEach((publication) => {
          const track = publication.track;
          if (track && track.kind !== 'data') {
            track.stop();
          }
        });
        room.disconnect();
      }

      cleanupVideoSessionState();

      router.replace(ROUTES.SESSIONS.path);
    } catch (error) {
      SentryService.captureError('Error exiting video call', error);
      cleanupVideoSessionState();

      router.replace(ROUTES.SESSIONS.path);
    }
  }, [room]);

  const handleEndCallPress = useCallback(() => {
    SentryService.captureInfo('Twilio: User pressed end call button');
    setIsEndCallModalVisible(true);
  }, []);

  const handleEndSessionAndExit = useCallback(async () => {
    SentryService.captureInfo('Twilio: User confirmed end session and exit');
    setIsEndCallModalVisible(false);
    await leaveSession();
  }, [leaveSession]);

  const handleExitCallOnly = useCallback(async () => {
    SentryService.captureInfo('Twilio: User selected exit call only');
    setIsEndCallModalVisible(false);
    await exitCallOnly();
  }, [exitCallOnly]);

  const handleExtendCall = useCallback(() => {
    SentryService.captureInfo('Twilio: User extended call');
    checkTimeOutRef();
    setIsTwoMinuteWarningModalVisible(false);
    setHasShownTwoMinuteWarning(true);
  }, []);

  const handleDismissTwoMinuteWarning = useCallback(() => {
    SentryService.captureInfo('Twilio: User dismissed 2-minute warning');
    setIsTwoMinuteWarningModalVisible(false);

    if (!activeConsultation?.endTime) {
      return;
    }

    checkTimeOutRef();

    const endTime = new Date(activeConsultation.endTime).getTime();
    const now = Date.now();
    const remainingMs = endTime - now;

    if (remainingMs > 0) {
      autoEndTimeoutRef.current = setTimeout(() => {
        handleEndSessionAndExit();
      }, remainingMs);
    } else {
      handleEndSessionAndExit();
    }
  }, [activeConsultation?.endTime, handleEndSessionAndExit]);

  // Handle other user disconnection
  useEffect(() => {
    if (otherUserDisconnected) {
      setOtherUserDisconnected(false);
      leaveSession();
    }
  }, [otherUserDisconnected, setOtherUserDisconnected, leaveSession]);

  // Monitor remaining time and show 2-minute warning modal
  useEffect(() => {
    if (
      consultationsLoading ||
      !activeConsultation?.endTime ||
      hasShownTwoMinuteWarning ||
      !activeSessionName ||
      isStylist
    ) {
      return;
    }

    const checkRemainingTime = () => {
      const endTime = new Date(activeConsultation.endTime).getTime();
      const now = Date.now();
      const remainingMs = endTime - now;
      const remainingSeconds = remainingMs / 1000;

      if (remainingSeconds <= 120 && remainingSeconds > 0) {
        setIsTwoMinuteWarningModalVisible(true);
        setHasShownTwoMinuteWarning(true);
      }
    };

    checkRemainingTime();
    const interval = setInterval(checkRemainingTime, 1000);

    return () => {
      clearInterval(interval);
    };
  }, [
    activeConsultation?.endTime,
    consultationsLoading,
    hasShownTwoMinuteWarning,
    activeSessionName,
    isStylist,
  ]);

  // Auto-dismiss modal and extend call if user doesn't interact
  useEffect(() => {
    if (
      !isTwoMinuteWarningModalVisible ||
      !activeConsultation?.endTime ||
      !activeSessionName ||
      isStylist
    ) {
      return;
    }

    const checkAndAutoDismiss = () => {
      const endTime = new Date(activeConsultation.endTime).getTime();
      const now = Date.now();
      const remainingMs = endTime - now;

      if (remainingMs <= 0) {
        handleExtendCall();
      }
    };

    const interval = setInterval(checkAndAutoDismiss, 1000);

    return () => {
      clearInterval(interval);
    };
  }, [
    isTwoMinuteWarningModalVisible,
    activeConsultation?.endTime,
    activeSessionName,
    isStylist,
    handleExtendCall,
  ]);

  // Reset warning state when session ends or changes
  useEffect(() => {
    if (!activeSessionName) {
      setHasShownTwoMinuteWarning(false);
      setIsTwoMinuteWarningModalVisible(false);
      if (autoEndTimeoutRef.current) {
        clearTimeout(autoEndTimeoutRef.current);
        autoEndTimeoutRef.current = undefined;
      }
    }
  }, [activeSessionName]);

  const onPressAudio = async () => {
    if (!room) {
      return;
    }

    const localParticipant = room.localParticipant;
    const audioPublication = Array.from(
      localParticipant.audioTracks.values(),
    )[0];
    const audioTrack = (audioPublication?.track as LocalAudioTrack) || null;

    if (audioTrack) {
      if (isAudioMuted) {
        audioTrack.enable();
        setIsAudioMuted(false);
      } else {
        audioTrack.disable();
        setIsAudioMuted(true);
      }
    }
  };

  const onPressVideo = async () => {
    if (!room) {
      return;
    }

    const localParticipant = room.localParticipant;
    const videoPublication = Array.from(
      localParticipant.videoTracks.values(),
    )[0];
    const videoTrack = (videoPublication?.track as LocalVideoTrack) || null;

    if (videoTrack) {
      if (isVideoMuted) {
        videoTrack.enable();
        setIsVideoMuted(false);
      } else {
        videoTrack.disable();
        setIsVideoMuted(true);
      }
    }
  };

  return (
    <OuterContainer isChatOpen={isChatPanelOpen}>
      <Header>
        <Image
          source={require('@/assets/images/EmmeLogo.png')}
          alt="Emme Logo"
          resizeMode="contain"
          style={{ width: 104, height: 34 }}
        />
        <Button
          text="End Call"
          onPress={handleEndCallPress}
          customStyle={endCallButtonStyles}
          customLabelStyle={{ fontSize: 13 }}
          size="small"
          compact={true}
        />
      </Header>
      <InnerContainer>
        <VideoLayout>
          <VideoWrapper isChatOpen={isChatPanelOpen}>
            <LocalVideoContainer ref={localVideoRef} />
            <NameLabel>
              {isAudioMuted && (
                <Image
                  source={require('@/assets/images/video/MutedIcon.png')}
                  alt="muted"
                  resizeMode="contain"
                  style={{ width: 11, height: 11, marginRight: 5 }}
                />
              )}
              You
            </NameLabel>
            {isVideoMuted && (
              <VideoOverlay>
                <Image
                  source={require('@/assets/images/video/EmptyViewIcon.png')}
                  alt="Video Off"
                  resizeMode="contain"
                  style={{ width: 76, height: 76 }}
                />
              </VideoOverlay>
            )}
          </VideoWrapper>
          <VideoWrapper isChatOpen={isChatPanelOpen}>
            <RemoteVideoContainer ref={remoteVideoRef} />
            {remoteParticipant && (
              <NameLabel>
                {isRemoteAudioMuted && (
                  <Image
                    source={require('@/assets/images/video/MutedIcon.png')}
                    alt="muted"
                    resizeMode="contain"
                    style={{ width: 11, height: 11, marginRight: 5 }}
                  />
                )}
                {extractDisplayNameFromIdentity(remoteParticipant.identity) ||
                  'Guest'}
              </NameLabel>
            )}
            {isRemoteVideoMuted && (
              <VideoOverlay>
                <Image
                  source={require('@/assets/images/video/EmptyViewIcon.png')}
                  alt="Video Off"
                  resizeMode="contain"
                  style={{ width: 76, height: 76 }}
                />
              </VideoOverlay>
            )}
          </VideoWrapper>
        </VideoLayout>

        <ControlsRow>
          <TimerWrapper isChatOpen={isChatPanelOpen}>
            <DurationTimer>{formattedTime}</DurationTimer>
          </TimerWrapper>
          <ButtonRow>
            <Pressable onPress={onPressAudio}>
              <Image
                style={controlImageStyles}
                source={
                  isAudioMuted
                    ? require('@/assets/images/video/AudioOffDesktopIcon.png')
                    : require('@/assets/images/video/AudioOnIcon.png')
                }
                resizeMode="contain"
              />
            </Pressable>
            <Pressable onPress={onPressVideo}>
              <Image
                style={controlImageStyles}
                source={
                  isVideoMuted
                    ? require('@/assets/images/video/VideoOffDesktopIcon.png')
                    : require('@/assets/images/video/VideoOnIcon.png')
                }
                resizeMode="contain"
              />
            </Pressable>
            <Pressable onPress={() => setIsChatPanelOpen(!isChatPanelOpen)}>
              <ChatButtonWrapper>
                <Image
                  style={
                    isChatPanelOpen
                      ? { width: 20, height: 20, tintColor: green }
                      : { width: 26, height: 26 }
                  }
                  source={
                    isChatPanelOpen
                      ? require('@/assets/images/CloseIcon.png')
                      : require('@/assets/images/video/ChatVideoIcon.png')
                  }
                  resizeMode="contain"
                />
              </ChatButtonWrapper>
            </Pressable>
          </ButtonRow>
        </ControlsRow>
      </InnerContainer>
      <EndCallModal
        isVisible={isEndCallModalVisible}
        setIsVisible={setIsEndCallModalVisible}
        onEndSessionAndExit={handleEndSessionAndExit}
        onExitCallOnly={handleExitCallOnly}
        isStylist={isStylist ?? false}
      />
      {!isStylist && (
        <TwoMinuteWarningModal
          isVisible={isTwoMinuteWarningModalVisible}
          setIsVisible={setIsTwoMinuteWarningModalVisible}
          onDismiss={handleDismissTwoMinuteWarning}
          onExtendCall={handleExtendCall}
        />
      )}
      {sessionChannelUrl && (
        <VideoChat
          channelUrl={sessionChannelUrl}
          isOpen={isChatPanelOpen}
          onClose={() => setIsChatPanelOpen(false)}
        />
      )}
    </OuterContainer>
  );
};

export default TwilioVideoFeed;

// Video layout constants
const VIDEO_DEFAULT_WIDTH = 640;
const VIDEO_DEFAULT_HEIGHT = 360;
const VIDEO_GAP = 7;
const VIDEO_TOTAL_WIDTH = VIDEO_DEFAULT_WIDTH * 2 + VIDEO_GAP; // 1287px

const OuterContainer = styled.div<{ isChatOpen?: boolean }>(
  ({ isChatOpen }) => ({
    position: 'fixed',
    top: 0,
    left: 0,
    right: isChatOpen ? VIDEO_CHAT_WEB_PANEL_WIDTH : 0,
    bottom: 0,
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: green,
    alignItems: 'center',
    maxWidth: '100vw',
    transition: 'right 0.3s ease-in-out',
  }),
);

const InnerContainer = styled.div({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  width: '100%',
  justifyContent: 'center',
});

const Header = styled.div({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '26px 44px 18.5px',
  borderBottom: `1px solid ${accent}`,
  maxWidth: '100%',
  width: '100%',
  boxSizing: 'border-box',
});

const VideoLayout = styled.div({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'center',
  alignItems: 'center',
  gap: 7,
});

const VideoWrapper = styled.div<{ isChatOpen?: boolean }>(({ isChatOpen }) => {
  let width = VIDEO_DEFAULT_WIDTH;
  let height = VIDEO_DEFAULT_HEIGHT;

  if (isChatOpen && typeof window !== 'undefined') {
    const availableWidth = window.innerWidth - VIDEO_CHAT_WEB_PANEL_WIDTH;
    // Need space for 2 videos side by side + gap between them + some padding
    const requiredWidth = VIDEO_TOTAL_WIDTH + 40; // 40px total padding

    if (availableWidth < requiredWidth) {
      // Scale down both videos proportionally to fit
      const maxWidthPerVideo = (availableWidth - VIDEO_GAP - 40) / 2;
      width = Math.max(320, Math.min(VIDEO_DEFAULT_WIDTH, maxWidthPerVideo)); // Min 320px
      height = Math.round(width * (9 / 16)); // Maintain 16:9 aspect ratio
    }
  }

  return {
    position: 'relative',
    width,
    height,
  };
});

const LocalVideoContainer = styled.div({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  backgroundColor: videoCellBackground,
  overflow: 'hidden',
  '& video': {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
});

const RemoteVideoContainer = styled.div({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  backgroundColor: black,
  overflow: 'hidden',
  '& video': {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
  },
});

const VideoOverlay = styled.div({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: videoCellBackground,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  zIndex: ZIndex.BottomLayer,
});

const NameLabel = styled.div({
  position: 'absolute',
  bottom: 0,
  left: 0,
  color: white,
  fontSize: 11.5,
  padding: 5,
  minWidth: 61,
  backgroundColor: lightGreen,
  zIndex: ZIndex.MiddleLayer,
  display: 'flex',
  ...FONT_STYLES.helvetica,
});

const ControlsRow = styled.div({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'center',
  alignItems: 'center',
  marginTop: 52,
  width: '100%',
  position: 'relative',
});

const TimerWrapper = styled.div<{ isChatOpen?: boolean }>(({ isChatOpen }) => {
  // Calculate padding to align timer with left edge of video layout
  let paddingCalc = `calc((100vw - ${VIDEO_TOTAL_WIDTH}px) / 2)`;

  if (isChatOpen && typeof window !== 'undefined') {
    const availableWidth = window.innerWidth - VIDEO_CHAT_WEB_PANEL_WIDTH;
    const padding = (availableWidth - VIDEO_TOTAL_WIDTH) / 2;
    paddingCalc = `${Math.max(0, padding)}px`;
  }

  return {
    position: 'absolute',
    left: 0,
    display: 'flex',
    justifyContent: 'flex-start',
    paddingLeft: paddingCalc,
    width: '100%',
    transition: 'padding-left 0.3s ease-in-out',
  };
});

const DurationTimer = styled.div({
  color: sand,
  fontSize: 20,
  letterSpacing: -0.2,
  ...FONT_STYLES.lora,
  marginLeft: 24,
});

const ButtonRow = styled.div({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'center',
  alignItems: 'center',
  gap: 12.5,
  width: '100%',
});

const ChatButtonWrapper = styled.div({
  width: 55,
  height: 55,
  borderRadius: '50%',
  backgroundColor: white,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
});

const controlImageStyles: StyleProp<ImageStyle> = {
  width: 55,
  height: 55,
};

const endCallButtonStyles: StyleProp<ViewStyle> = {
  backgroundColor: red,
  width: 100,
  padding: 0,
};
